import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store';
import { listPublicPayloads, type PayloadEntry } from '../api-payloads';
import { displayUrl, listMyShares, type ShareSummary } from '../api';

interface Item {
  id: string;
  group: 'Actions' | 'Payloads' | 'Shares';
  label: string;
  hint?: string;
  run: () => void;
}

function rowId(): string {
  return Math.random().toString(36).slice(2, 10);
}

// Cmd+K: one search box over actions, payload presets and your shares.
export function CommandPalette() {
  const open = useStore((s) => s.paletteOpen);
  const setOpen = useStore((s) => s.setPaletteOpen);
  const domainBase = useStore((s) => s.domainBase);
  const domainSub = useStore((s) => s.domainSub);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [payloads, setPayloads] = useState<PayloadEntry[]>([]);
  const [shares, setShares] = useState<ShareSummary[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    void listPublicPayloads().then(setPayloads);
    void listMyShares().then(setShares);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const st = useStore.getState();
    const actions: Item[] = [
      { id: 'new', group: 'Actions', label: 'New document', hint: 'clear the editor', run: () => st.newDocument() },
      { id: 'save', group: 'Actions', label: 'Save', hint: '⌘S', run: () => window.dispatchEvent(new CustomEvent('xsscool:save')) },
      { id: 'save-new', group: 'Actions', label: 'Save as new share', hint: '⌘⇧S', run: () => window.dispatchEvent(new CustomEvent('xsscool:save', { detail: { forceNew: true } })) },
      { id: 'tab-body', group: 'Actions', label: 'Go to Body', hint: '⌘1', run: () => st.setTab('body') },
      { id: 'tab-headers', group: 'Actions', label: 'Go to Headers', hint: '⌘2', run: () => st.setTab('headers') },
      { id: 'tab-captures', group: 'Actions', label: 'Go to Captures', hint: '⌘3', run: () => st.setTab('captures') },
      { id: 'library', group: 'Actions', label: 'Open payload library', hint: '⌘B', run: () => st.setPayloadsOpen(true) },
      { id: 'preview', group: 'Actions', label: `${st.previewOpen ? 'Hide' : 'Show'} preview`, run: () => st.setPreviewOpen(!st.previewOpen) },
      { id: 'shortcuts', group: 'Actions', label: 'Keyboard shortcuts', hint: '?', run: () => st.setHelpOpen(true) },
      { id: 'submit', group: 'Actions', label: 'Submit to payload library', run: () => st.setSubmitOpen(true) },
      { id: 'docs', group: 'Actions', label: 'Open docs', run: () => window.open('/docs', '_blank', 'noopener') },
    ];
    const presets: Item[] = payloads.map((p) => ({
      id: `payload-${p.id}`,
      group: 'Payloads',
      label: p.title,
      hint: p.description,
      run: () =>
        st.applyPreset(
          { ...p.state, headers: p.state.headers.map((h) => ({ id: rowId(), name: h.name, value: h.value })) },
          { label: p.title },
        ),
    }));
    const mine: Item[] = shares.map((sh) => ({
      id: `share-${sh.id}`,
      group: 'Shares',
      label: sh.name || sh.id,
      hint: sh.preview,
      run: () => window.location.assign(displayUrl(`/e/${sh.id}`, { domainBase, domainSub })),
    }));
    return [...actions, ...presets, ...mine];
  }, [payloads, shares, domainBase, domainSub, open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.label.toLowerCase().includes(q) || (i.hint ?? '').toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => setCursor(0), [query]);

  if (!open) return null;

  const pick = (item: Item) => {
    setOpen(false);
    item.run();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(filtered.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[cursor];
      if (item) pick(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  let lastGroup = '';
  return (
    <div className="help-overlay palette-overlay" role="dialog" aria-modal="true" aria-label="Command palette" onClick={() => setOpen(false)}>
      <div className="palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="palette-input"
          placeholder="Type a command, payload or share name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          spellCheck={false}
        />
        <ul className="palette-list" role="listbox">
          {filtered.length === 0 && <li className="palette-empty">No matches</li>}
          {filtered.map((item, i) => {
            const showGroup = item.group !== lastGroup;
            lastGroup = item.group;
            return (
              <li key={item.id}>
                {showGroup && <div className="palette-group">{item.group}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  className={'palette-item' + (i === cursor ? ' active' : '')}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => pick(item)}
                >
                  <span className="palette-label">{item.label}</span>
                  {item.hint && <span className="palette-hint">{item.hint}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
